import { MonthKey, WeekdayKey } from '../common/calendar';

export interface WeekdayI18n {
  [WeekdayKey.SUN]: string;
  [WeekdayKey.MON]: string;
  [WeekdayKey.TUE]: string;
  [WeekdayKey.WED]: string;
  [WeekdayKey.THU]: string;
  [WeekdayKey.FRI]: string;
  [WeekdayKey.SAT]: string;
}

export interface MonthI18n {
  [MonthKey.JAN]: string;
  [MonthKey.FEB]: string;
  [MonthKey.MAR]: string;
  [MonthKey.APR]: string;
  [MonthKey.MAY]: string;
  [MonthKey.JUNE]: string;
  [MonthKey.JULY]: string;
  [MonthKey.AUG]: string;
  [MonthKey.SEPT]: string;
  [MonthKey.OCT]: string;
  [MonthKey.NOV]: string;
  [MonthKey.DEC]: string;
}

export interface DateFormatI18n {
  fullDetailed: string;
  full: string;
  date: string;
  monthAndYear: string;
  dateAndMonthFull: string;
  dateAndMonth: string;
}

export interface DateI18n {
  weekdays: WeekdayI18n;
  weekdaysShort: WeekdayI18n;
  months: MonthI18n;
  monthsShort: MonthI18n;
  formats: DateFormatI18n;
}

export const DEFAULT_DATE_I18N: Readonly<DateI18n> = Object.freeze({
  formats: {
    fullDetailed: 'EE dd MMMM yyyy',
    full: 'E d MMM yyyy',
    date: 'd MMM yyyy',
    monthAndYear: 'MMMM yyyy',
    dateAndMonthFull: 'E d MMM',
    dateAndMonth: 'd MMM',
  },
  weekdaysShort: {
    mon: 'mon',
    tue: 'tue',
    wed: 'wed',
    thu: 'thu',
    fri: 'fri',
    sat: 'sat',
    sun: 'sun',
  },
  weekdays: {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
  },
  monthsShort: {
    jan: 'Jan',
    feb: 'Feb',
    mar: 'Mar',
    apr: 'Apr',
    may: 'May',
    june: 'Jun',
    july: 'Jul',
    aug: 'Aug',
    sept: 'Sep',
    oct: 'Oct',
    nov: 'Nov',
    dec: 'Dec',
  },
  months: {
    jan: 'January',
    feb: 'February',
    mar: 'March',
    apr: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    aug: 'August',
    sept: 'September',
    oct: 'October',
    nov: 'November',
    dec: 'December',
  },
});

export interface NumberI18n {
  thousandSeparator: string;
  decimalSeparator: string;
  currencyFormat: string;
  currency: string;
  thousandAbbreviation: string;
  millionAbbreviation: string;
}

export const DEFAULT_NUMBER_I18N: Readonly<NumberI18n> = Object.freeze({
  thousandSeparator: ',',
  decimalSeparator: '.',
  currencyFormat: '1 $XYZ',
  currency: 'EUR',
  thousandAbbreviation: 'k',
  millionAbbreviation: 'M',
});

export interface IntlContextWithDictionary {
  intl: {
    scope?: string;
    definition: any;
    mark: boolean;
    dictionary?: {
      date: DateI18n;
      number: NumberI18n;
    };
  };
}

export const dateI18n = (ctx: IntlContextWithDictionary): DateI18n => ctx?.intl?.dictionary?.date || DEFAULT_DATE_I18N;
export const numberI18n = (ctx: IntlContextWithDictionary): NumberI18n =>
  ctx?.intl?.dictionary?.number || DEFAULT_NUMBER_I18N;

export interface Plural {
  none: string;
  one: string;
  many: string;
}

type DotPrefix<T extends string> = T extends '' ? '' : `.${T}`;

export type DotNestedKeys<T> =
  // eslint-disable-next-line @typescript-eslint/ban-types
  (
    T extends object
      ? { [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}` }[Exclude<keyof T, symbol>]
      : ''
  ) extends infer D
    ? Extract<D, string>
    : never;
